/**
 * [PURPOSE]: Routes LevelUpWizard's accumulated highLevelSpellPicks array
 *   to the correct character-data field based on (className, classLevel).
 *
 *   Spec A Task 6: Wizard L18 (Spell Mastery) and L20 (Signature Spells)
 *   now write to the generic playerStats.customClassPicks bucket so the
 *   atWillSpell / perRestSpell effect-type pipeline can resolve them.
 *   Previously these wrote to Wizard-specific paths
 *   (playerStats.spellMastery / playerStats.signatureSpells) — those paths
 *   are migrated away in v1→v2 (characterMigrations.migrateV1ToV2).
 *
 *   Warlock Mystic Arcanum (L11/13/15/17) and Bard Magical Secrets
 *   (L10/14/18) paths preserved unchanged — they still have feature-
 *   specific consumers and aren't affected by Spec A.
 *
 * [WHY]: Extracted from App.jsx commit logic so unit tests drive REAL
 *   routing code (Rule 22) instead of stubbing the heavy commit callback.
 *
 * [INTEGRATION]: App.jsx commit handler imports and calls this with the
 *   in-progress `updated` playerStats object. The helper returns a new
 *   playerStats with the picks written to the right path; App.jsx then
 *   reassigns to the local `updated` variable.
 */

/**
 * @param {Object} updated In-progress playerStats object (caller's `updated`)
 * @param {string} ctxClass Class name from LevelUpWizard (e.g., 'Wizard')
 * @param {number} ctxLevel New class level (e.g., 18)
 * @param {Array<string>} picks Player's selected spell names
 * @param {{ className?: string, featureId?: string } | null} [pickConfig]
 *   Optional — when present and carries {className, featureId} from the
 *   AT-1a generic detector, writes picks to
 *   customClassPicks[className][featureId].  Callers that don't pass this
 *   (SRD commit paths) are unaffected — the SRD branches run first.
 * @returns {Object} New playerStats with picks routed to the right field
 */
export function routeHighLevelSpellPicks(updated, ctxClass, ctxLevel, picks, pickConfig) {
  if (!Array.isArray(picks) || picks.length === 0) return updated;
  const safeUpdated = { ...(updated || {}) };

  if (ctxClass === 'Warlock' && [11, 13, 15, 17].includes(ctxLevel)) {
    // Mystic Arcanum — single pick keyed by spell level
    const arcanumLevel = ctxLevel === 11 ? 6 : ctxLevel === 13 ? 7 : ctxLevel === 15 ? 8 : 9;
    safeUpdated.mysticArcanum = { ...(safeUpdated.mysticArcanum || {}) };
    safeUpdated.mysticArcanum[arcanumLevel] = picks[0];
    return safeUpdated;
  }

  if (ctxClass === 'Bard' && [10, 14, 18].includes(ctxLevel)) {
    // Magical Secrets — append + dedupe across levels
    const existing = Array.isArray(safeUpdated.magicalSecrets) ? safeUpdated.magicalSecrets : [];
    safeUpdated.magicalSecrets = Array.from(new Set([...existing, ...picks]));
    return safeUpdated;
  }

  if (ctxClass === 'Wizard' && ctxLevel === 18) {
    // [Spec A Task 6]: Spell Mastery → customClassPicks.Wizard.spell_mastery
    //   (was: playerStats.spellMastery = { firstLevel, secondLevel } pre-Spec-A;
    //   migrated by characterMigrations.migrateV1ToV2 for existing saves)
    safeUpdated.customClassPicks = { ...(safeUpdated.customClassPicks || {}) };
    safeUpdated.customClassPicks.Wizard = { ...(safeUpdated.customClassPicks.Wizard || {}) };
    safeUpdated.customClassPicks.Wizard.spell_mastery = picks.filter(Boolean);
    return safeUpdated;
  }

  if (ctxClass === 'Wizard' && ctxLevel === 20) {
    // [Spec A Task 6]: Signature Spells → customClassPicks.Wizard.signature_spells
    safeUpdated.customClassPicks = { ...(safeUpdated.customClassPicks || {}) };
    safeUpdated.customClassPicks.Wizard = { ...(safeUpdated.customClassPicks.Wizard || {}) };
    safeUpdated.customClassPicks.Wizard.signature_spells = picks.filter(Boolean);
    return safeUpdated;
  }

  // [A49 AT-1b — 2026-06-03]: Generic CW fallback — fires when the pick step
  //   was emitted by the AT-1a detectPickedAtLevelUpEffects detector and
  //   carries {className, featureId}. Writes picks to
  //   customClassPicks[className][featureId] so effectCalculator.getAtWillSpells
  //   can read them — same storage target the SRD Wizard Spell Mastery path uses.
  //
  //   ADDITIVE: only fires when no SRD branch above matched (all SRD branches
  //   return early). This path is also reached by SRD classes at levels not
  //   covered by the 3 SRD branches, so we guard on pickConfig presence.
  //
  //   [RULE 11/16/17]: No class/feature name strings — className and featureId
  //   come from the effect's own declarations (via pickConfig from AT-1a).
  if (pickConfig && pickConfig.className && pickConfig.featureId) {
    const { className: pcClass, featureId } = pickConfig;
    safeUpdated.customClassPicks = { ...(safeUpdated.customClassPicks || {}) };
    safeUpdated.customClassPicks[pcClass] = { ...(safeUpdated.customClassPicks[pcClass] || {}) };
    safeUpdated.customClassPicks[pcClass][featureId] = picks.filter(Boolean);
    return safeUpdated;
  }

  // Non-matching (className, level) — return unchanged
  return safeUpdated;
}
